import { useState, type FormEvent } from "react";
import { ArrowLeft, Eye, EyeOff, LockKeyhole, Mail } from "lucide-react";
import { AuthLayout } from "../components/AuthLayout";
import { Button } from "../components/Button";
import { FieldInput } from "../components/Field";
import { MessageBanner } from "../components/MessageBanner";
import { useSession } from "../lib/session/SessionContext";

type Mode = "login" | "forgot";

export function LoginScreen() {
  const { login, resetPassword, error, clearError } = useSession();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const switchMode = (next: Mode) => {
    setMode(next);
    setLocalError(null);
    setResetSent(false);
    clearError();
  };

  const submitLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!email.trim() || !password) {
      setLocalError("Preencha e-mail e senha.");
      return;
    }
    setBusy(true);
    try {
      await login(email.trim(), password);
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!email.trim()) {
      setLocalError("Informe o e-mail da sua conta.");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(email.trim());
      setResetSent(true);
    } catch {
      // erro já normalizado pelo contexto
    } finally {
      setBusy(false);
    }
  };

  const message = localError ?? error;

  if (mode === "forgot") {
    return (
      <AuthLayout
        title="Recuperar acesso"
        subtitle="Enviaremos um link para você criar uma nova senha."
        footer={<span>Apogeu App · alunos e professores</span>}
      >
        <form className="auth-form" onSubmit={submitForgot}>
          <FieldInput
            label="E-mail"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="voce@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            trailing={<Mail size={18} className="field__icon" aria-hidden="true" />}
          />
          {message && <MessageBanner>{message}</MessageBanner>}
          {resetSent && (
            <MessageBanner tone="success">
              Se houver uma conta com este e-mail, o link de recuperação chegará em instantes.
            </MessageBanner>
          )}
          <Button type="submit" fullWidth disabled={busy}>
            {busy ? "Enviando..." : "Enviar link de recuperação"}
          </Button>
          <button type="button" className="link-button link-button--center" onClick={() => switchMode("login")}>
            <ArrowLeft size={15} aria-hidden="true" /> Voltar para entrar
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Bem-vindo de volta"
      subtitle="Entre para acompanhar suas aulas, reservas e treinos no seu CT."
      footer={<span>Apogeu App · alunos e professores</span>}
    >
      <form className="auth-form" onSubmit={submitLogin}>
        <FieldInput
          label="E-mail"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="voce@email.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          trailing={<Mail size={18} className="field__icon" aria-hidden="true" />}
        />
        <FieldInput
          label="Senha"
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          placeholder="Sua senha"
          value={password}
          onChange={e => setPassword(e.target.value)}
          trailing={
            <button
              type="button"
              className="field__action"
              onClick={() => setShowPassword(value => !value)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />
        {message && <MessageBanner>{message}</MessageBanner>}
        <Button type="submit" fullWidth disabled={busy}>
          <LockKeyhole size={17} aria-hidden="true" />
          {busy ? "Entrando..." : "Entrar"}
        </Button>
        <button type="button" className="link-button link-button--center" onClick={() => switchMode("forgot")}>
          Esqueci minha senha
        </button>
      </form>
      <p className="auth-note">
        Recebeu um convite do seu CT? Abra o link recebido no e-mail para criar seu acesso.
      </p>
    </AuthLayout>
  );
}
